import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import SearchableCombobox from '@/components/ui/searchable-combobox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Múltiples Razas Selector
 * 
 * Props:
 *   tipoGanado     - string, selected tipoGanado
 *   totalCantidad  - number, total lote quantity
 *   value          - Array of {nombre: string, cantidad: number}
 *   onChange       - callback(Array)
 *   errors         - validation errors
 *   disabled       - boolean
 */
const MultiRazaSelector = ({ tipoGanado, totalCantidad, value = [], onChange, errors = {}, disabled = false }) => {
  const [razas, setRazas] = useState([]);
  const [tipoGanadoId, setTipoGanadoId] = useState(null);

  // Quick add modal
  const [showAddRaza, setShowAddRaza] = useState(false);
  const [newRazaNombre, setNewRazaNombre] = useState('');
  const [addingRaza, setAddingRaza] = useState(false);

  useEffect(() => {
    if (tipoGanado) {
      loadTipoGanadoAndRazas(tipoGanado);
    } else {
      setRazas([]);
      setTipoGanadoId(null);
    }
  }, [tipoGanado]);

  const loadTipoGanadoAndRazas = async (nombre) => {
    try {
      // Find ID
      const tgRes = await axios.get(`${API}/tipoGanados`);
      const found = tgRes.data.find(m => m.nombre.toLowerCase() === nombre.toLowerCase());
      if (found) {
        setTipoGanadoId(found.id);
        const rRes = await axios.get(`${API}/tipoGanados/${found.id}/razas`);
        setRazas(rRes.data);
      }
    } catch (error) {
      console.error('Error loading razas:', error);
    }
  };

  const getRemainingQty = () => {
    const sum = value.reduce((acc, curr) => acc + (Number(curr.cantidad) || 0), 0);
    const qty = Number(totalCantidad) || 0;
    return Math.max(0, qty - sum);
  };

  const handleAddRow = () => {
    const remaining = getRemainingQty();
    onChange([...value, { nombre: '', cantidad: remaining }]);
  };

  const handleUpdateRow = (index, field, val) => {
    const newVal = [...value];
    newVal[index][field] = val;
    onChange(newVal);
  };

  const handleDeleteRow = (index) => {
    const newVal = value.filter((_, i) => i !== index);
    onChange(newVal);
  };

  const handleCreateRaza = async () => {
    if (!newRazaNombre.trim()) {
      toast.error('El nombre de la raza es requerido');
      return;
    }
    if (!tipoGanadoId) return;

    setAddingRaza(true);
    try {
      const response = await axios.post(`${API}/razas`, {
        tipoGanadoId,
        nombre: newRazaNombre.trim()
      });
      toast.success(`Raza "${response.data.nombre}" creada`);
      setShowAddRaza(false);
      setNewRazaNombre('');
      
      const rRes = await axios.get(`${API}/tipoGanados/${tipoGanadoId}/razas`);
      setRazas(rRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear raza');
    } finally {
      setAddingRaza(false);
    }
  };

  const razaOptions = razas.map(r => ({
    value: r.nombre,
    label: r.nombre
  }));

  // Helper validation
  const sum = value.reduce((acc, curr) => acc + (Number(curr.cantidad) || 0), 0);
  const qty = Number(totalCantidad) || 0;
  const isOver = qty > 0 && sum > qty;
  const isUnder = qty > 0 && sum < qty;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Composición de Razas</Label>
        {!disabled && tipoGanado && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAddRaza(true)}
              title="Nueva raza en sistema"
            >
              + Nueva Raza
            </Button>
          </div>
        )}
      </div>

      {!tipoGanado ? (
        <p className="text-sm text-muted-foreground">Primero seleccione un Tipo de Ganado.</p>
      ) : (
        <div className="space-y-3">
          {value.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay razas asignadas. Agregue al menos una.</p>
          ) : (
            value.map((item, index) => (
              <div key={index} className="flex items-center gap-3 bg-secondary/20 p-2 rounded-md">
                <div className="flex-1">
                  <SearchableCombobox
                    options={razaOptions}
                    value={item.nombre}
                    onSelect={(val) => handleUpdateRow(index, 'nombre', val)}
                    placeholder="Buscar raza..."
                    emptyText="No se encontraron razas."
                    disabled={disabled}
                  />
                </div>
                <div className="w-32">
                  <Input
                    type="number"
                    min="1"
                    placeholder="Cantidad"
                    value={item.cantidad}
                    onChange={(e) => handleUpdateRow(index, 'cantidad', e.target.value ? parseInt(e.target.value, 10) : '')}
                    disabled={disabled}
                  />
                </div>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => handleDeleteRow(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))
          )}

          {!disabled && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleAddRow}
              className="w-full border border-dashed"
            >
              <Plus className="h-4 w-4 mr-2" />
              Añadir Raza al Lote
            </Button>
          )}

          {qty > 0 && (
             <div className="flex justify-between items-center text-sm px-1">
               <span className="text-muted-foreground">Total asignado: {sum} / {qty}</span>
               {isOver && <span className="text-destructive font-medium">Límite excedido</span>}
               {isUnder && <span className="text-amber-600 font-medium">Faltan asignar</span>}
               {!isOver && !isUnder && sum > 0 && <span className="text-green-600 font-medium">Cantidades exactas ✓</span>}
             </div>
          )}
          {errors.razas && <p className="text-sm text-destructive">{errors.razas}</p>}
        </div>
      )}

      <Dialog open={showAddRaza} onOpenChange={setShowAddRaza}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Nueva Raza</DialogTitle>
            <DialogDescription>
              Añade una raza para el tipo <strong>{tipoGanado}</strong> al catálogo del sistema.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="newRaza">Nombre de la Raza</Label>
              <Input
                id="newRaza"
                placeholder="Ej. Hereford"
                value={newRazaNombre}
                onChange={(e) => setNewRazaNombre(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateRaza();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRaza(false)} disabled={addingRaza}>
              Cancelar
            </Button>
            <Button onClick={handleCreateRaza} disabled={addingRaza || !newRazaNombre.trim()}>
              {addingRaza ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MultiRazaSelector;
